const PDF_LOADING_HTML = `<!DOCTYPE html>
<html lang="zh-TW">
<head>
  <meta charset="UTF-8" />
  <title>載入 PDF</title>
  <style>
    body {
      margin: 0;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      color: #444;
      background: #f6f7f9;
    }
  </style>
</head>
<body>正在載入 PDF…</body>
</html>`;

const PDF_BLOB_REVOKE_MS = 30 * 60 * 1000;

const isPdfBlob = (blob) =>
    blob instanceof Blob &&
    blob.size > 0 &&
    (blob.type === 'application/pdf' ||
        blob.type === 'application/octet-stream');

const FRIENDLY_PDF_MISSING = '找不到契約檔案，可能尚未產生或已失效。';

const looksLikeStorageMissing = (text) =>
    /NoSuchKey|No such object|does not exist|AccessDenied|NotFound|簽署 PDF 不存在|預覽 PDF 不存在/i.test(
        String(text || '')
    );

const parseBlobErrorMessage = async (blob, fallback = '無法載入 PDF') => {
    if (!(blob instanceof Blob)) {
        return fallback;
    }

    try {
        const text = await blob.text();
        if (looksLikeStorageMissing(text)) {
            return FRIENDLY_PDF_MISSING;
        }
        try {
            const json = JSON.parse(text);
            const msg = json.message || json.error || '';
            if (looksLikeStorageMissing(msg)) {
                return FRIENDLY_PDF_MISSING;
            }
            // 避免把整段技術堆疊丟給使用者
            if (
                typeof msg === 'string' &&
                msg.length &&
                msg.length < 120 &&
                !/Exception|Trace|NoSuch/i.test(msg)
            ) {
                return msg;
            }
            return fallback;
        } catch {
            return looksLikeStorageMissing(text)
                ? FRIENDLY_PDF_MISSING
                : fallback;
        }
    } catch {
        return fallback;
    }
};

/**
 * 在使用者點擊當下先開空白分頁，再串流 PDF，避免彈出視窗被擋與 blob 過早失效。
 * @param {() => Promise<Blob>} fetchBlob
 * @param {string} [loadingTitle]
 */
export const openBlobPdfInNewTab = async (
    fetchBlob,
    loadingTitle = '契約 PDF'
) => {
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
        throw new Error('請允許彈出視窗以預覽 PDF');
    }

    try {
        popup.document.open();
        popup.document.write(
            PDF_LOADING_HTML.replace('載入 PDF', loadingTitle)
        );
        popup.document.close();
    } catch {
        // 部分瀏覽器可能限制 document.write，仍可繼續載入
    }

    try {
        const blob = await fetchBlob();

        if (!isPdfBlob(blob)) {
            const message = await parseBlobErrorMessage(blob);
            popup.close();
            throw new Error(message);
        }

        const url = URL.createObjectURL(blob);
        popup.location.replace(url);
        setTimeout(() => URL.revokeObjectURL(url), PDF_BLOB_REVOKE_MS);
    } catch (error) {
        if (!popup.closed) {
            popup.close();
        }
        throw error;
    }
};

/**
 * 直接下載 PDF blob
 * @param {Blob} blob
 * @param {string} filename
 */
export const downloadBlobPdf = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
